import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  FileInput,
  FormField,
  Modal,
  SpaceBetween,
} from '@cloudscape-design/components';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

const MAX_FILE_SIZE = 10 * 1024 * 1024;

export type BulkScorecardUploadModalProps = {
  visible: boolean;
  onDismiss: () => void;
  onUpload: (file: File) => Promise<void>;
  onUploaded: () => void;
};

export function validateBulkScorecardFile(file: File | null): string | undefined {
  if (!file) return undefined;
  if (!file.name.toLowerCase().endsWith('.xlsx')) return 'Choose an .xlsx Excel file.';
  if (file.size > MAX_FILE_SIZE) return 'Choose a file no larger than 10 MiB.';
  return undefined;
}

export function BulkScorecardUploadModal({
  visible,
  onDismiss,
  onUpload,
  onUploaded,
}: BulkScorecardUploadModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const fileError = validateBulkScorecardFile(file);

  const upload = useMutation({
    mutationFn: onUpload,
    onSuccess: () => {
      setFile(null);
      onUploaded();
      onDismiss();
    },
  });

  const dismiss = () => {
    if (upload.isPending) return;
    upload.reset();
    setFile(null);
    onDismiss();
  };

  return (
    <Modal
      visible={visible}
      onDismiss={dismiss}
      closeAriaLabel="Close upload"
      header="Upload Excel file"
      size="medium"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={dismiss} disabled={upload.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => file && !fileError && upload.mutate(file)}
              disabled={!file || Boolean(fileError)}
              loading={upload.isPending}
            >
              Upload
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        <SpaceBetween size="m">
          <Box>
            Use the first worksheet of an <strong>.xlsx</strong> file no larger than 10 MiB.
          </Box>
          <ColumnLayout columns={1} variant="text-grid">
            <SpaceBetween size="xxs">
              <Box variant="awsui-key-label">Required columns</Box>
              <Box>Account Name, Street Address, City, State, Zip</Box>
            </SpaceBetween>
          </ColumnLayout>
          <Box>
            Name is also accepted instead of Account Name. All other columns are preserved, and
            rows missing a required value include an error in the downloaded workbook.
          </Box>
        </SpaceBetween>

        <FormField label="Excel file" errorText={fileError}>
          <FileInput
            value={file ? [file] : []}
            onChange={({ detail }) => {
              upload.reset();
              setFile(detail.value[0] ?? null);
            }}
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          >
            Choose file
          </FileInput>
        </FormField>

        {upload.isError ? (
          <Alert type="error" header="Upload failed">
            {upload.error instanceof Error
              ? upload.error.message
              : 'The workbook could not be uploaded. Try again.'}
          </Alert>
        ) : null}
      </SpaceBetween>
    </Modal>
  );
}
